import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import MyPropertyCard from "../components/MyPropertyCard";
import ConfirmationModal from "../components/ConfirmationModal";
import { toast } from "react-hot-toast";

const MyProperties = () => {
    const [propiedades, setPropiedades] = useState([]);
    const [informesSet, setInformesSet] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [modalState, setModalState] = useState({ isOpen: false, propertyId: null });

    const navigate = useNavigate();

    useEffect(() => {
        const checkUser = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/users/current-user`, { credentials: "include" });
                const data = await res.json();
                if (data.user) {
                    setIsLoggedIn(true);
                } else {
                    navigate('/');
                }
            } catch (err) {
                console.error("Error checking user:", err);
                navigate('/');
            }
        };

        checkUser();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [propRes, informesRes] = await Promise.allSettled([
                    fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/mis-propiedades`, { credentials: "include" }),
                    fetch(`${window.REACT_APP_API_ENDPOINT}/api/v2/mis-informes`, { credentials: "include" }),
                ]);

                if (propRes.status === 'fulfilled' && propRes.value.ok) {
                    const data = await propRes.value.json();
                    setPropiedades(data);
                } else {
                    throw new Error('Error fetching properties');
                }

                if (informesRes.status === 'fulfilled' && informesRes.value.ok) {
                    const data = await informesRes.value.json();
                    const ids = new Set((data.informes || []).map((i) => i.propiedad_id));
                    setInformesSet(ids);
                }
            } catch (err) {
                console.error("Error fetching properties:", err);
                setError("Error al cargar tus propiedades");
            } finally {
                setLoading(false);
            }
        };

        if (isLoggedIn) {
            fetchData();
        }
    }, [isLoggedIn]);

    const openDeleteModal = (id) => {
        setModalState({ isOpen: true, propertyId: id });
    };

    const closeDeleteModal = () => {
        setModalState({ isOpen: false, propertyId: null });
    };

    const handleConfirmDelete = async () => {
        if (!modalState.propertyId) return;

        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${modalState.propertyId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'No se pudo eliminar la propiedad');
            }

            setPropiedades(prev => prev.filter(p => p.id !== modalState.propertyId));
            toast.success('Propiedad eliminada con éxito');

        } catch (err) {
            console.error("Error deleting property:", err);
            toast.error(err.message || 'Error al eliminar la propiedad');
        } finally {
            closeDeleteModal();
        }
    };

    if (loading) {
        return <div className="container mx-auto p-6">Cargando...</div>;
    }

    if (error) {
        return (
            <div className="container mx-auto p-6">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">Mis Propiedades</h1>
            {propiedades.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-600">No tienes propiedades publicadas.</p>
                    <button
                        onClick={() => navigate('/propiedades/nueva')}
                        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Añadir nueva propiedad
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {propiedades.map((prop) => (
                        <div key={prop.id} className="relative">
                            <MyPropertyCard
                                prop={prop}
                                onDelete={openDeleteModal}
                            />
                            {informesSet.has(prop.id) && (
                                <div className="mt-2 px-1">
                                    <Link
                                        to={`/analizarpropiedad/${prop.public_id}/informe-detalle`}
                                        className="block w-full text-center text-sm bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded hover:bg-blue-100"
                                    >
                                        Ver informe
                                    </Link>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <ConfirmationModal
                isOpen={modalState.isOpen}
                onClose={closeDeleteModal}
                onConfirm={handleConfirmDelete}
                title="Confirmar Eliminación"
                message="¿Estás seguro de que quieres eliminar esta propiedad? Esta acción no se puede deshacer."
                confirmText="Sí, Eliminar"
            />
        </div>
    );
};

export default MyProperties; 